import { useEffect, useRef } from 'react'

import {
  IconCloudLock,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDots,
  IconFaceId,
  IconGavel,
  IconHome,
  IconLock,
  IconLogout,
  IconRefresh,
  IconShieldLock,
  IconUsers,
} from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from '@/components/common/responsive-dialog'
import { UpsellDialogShell } from '@/components/common/upsell'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { PLAN_VISUAL_CONFIG } from '@/lib/plans'
import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context'

import locales from './locales'
import {
  trackPlanPaywallConverted,
  trackPlanPaywallHomeClicked,
  trackPlanPaywallLogoutClicked,
  trackPlanPaywallOpened,
} from './plan-paywall-dialog.tracking'

// Plan paywall uses the Business (Pro) visual identity (the plan users trial)
const visual = PLAN_VISUAL_CONFIG.Pro

interface PlanPaywallDialogProps {
  open: boolean
  pagePath: string
}

interface PlanPaywallDialogContentProps {
  onLogout: () => void
  onGoHome: () => void
}

interface PlanPaywallDialogAdminProps {
  onLogout: () => void
  onSubscribe: () => void
  isFormerPaidSubscriber: boolean
}

/**
 * Exits offered to whoever cannot subscribe from here (managed accounts and
 * non-administrators). Going home is a real way out because the provider
 * suppresses this paywall on `/`, so it does not bounce straight back.
 */
function ExitButtons({
  onGoHome,
  onLogout,
}: {
  onGoHome: () => void
  onLogout: () => void
}) {
  const { t } = useComponentTranslation(locales)
  return (
    <>
      <Button variant="outline" onClick={onGoHome} className="gap-2">
        <IconHome className="h-4 w-4" />
        {t('goHome')}
      </Button>
      <Button variant="ghost" onClick={onLogout} className="gap-2">
        <IconLogout className="h-4 w-4" />
        {t('logout')}
      </Button>
    </>
  )
}

function NoPlanIconHeader() {
  return (
    <div className="bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-2xl">
      <IconLock className="text-muted-foreground h-8 w-8" />
    </div>
  )
}

// Generic Signater highlights (no plan name) shown above the subscribe CTA.
const PAYWALL_FEATURES: Array<{
  icon: React.ComponentType<{ className?: string }>
  key: string
  muted?: boolean
}> = [
  { icon: IconGavel, key: 'legalValidity' },
  { icon: IconUsers, key: 'unlimitedUsers' },
  { icon: IconShieldLock, key: 'multiFactorAuth' },
  { icon: IconFaceId, key: 'livenessSelfie' },
  { icon: IconCloudLock, key: 'secureStorage' },
  { icon: IconDeviceMobile, key: 'mobileAccess' },
  { icon: IconDeviceDesktop, key: 'modernInterface' },
  { icon: IconDots, key: 'andMore', muted: true },
]

function PaywallFeatures() {
  const { t } = useComponentTranslation(locales)
  return (
    <div>
      <p className="text-muted-foreground mb-3 text-center text-xs font-medium tracking-wide uppercase">
        {t('featuresTitle')}
      </p>
      <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">
        {PAYWALL_FEATURES.map(({ icon: Icon, key, muted }) => (
          <div key={key} className="flex items-center gap-2.5">
            <Icon
              className={cn(
                'size-4 shrink-0',
                muted ? 'text-muted-foreground' : visual.iconColor,
              )}
              aria-hidden="true"
            />
            <span className={cn('text-sm', muted && 'text-muted-foreground')}>
              {t(`features.${key}`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * PlanPaywallDialogManaged - For users managed by an administrator
 * Shows: title + description + contact account manager message
 */
function PlanPaywallDialogManaged({
  onLogout,
  onGoHome,
}: PlanPaywallDialogContentProps) {
  const { t } = useComponentTranslation(locales)
  return (
    <UpsellDialogShell
      icon={<NoPlanIconHeader />}
      title={t('title')}
      description={t('description')}
      contactAdmin={{
        title: t('managedContactAdmin'),
        description: t('managedDescription'),
      }}
      actions={<ExitButtons onGoHome={onGoHome} onLogout={onLogout} />}
    />
  )
}

/**
 * PlanPaywallDialogUser - For non-administrator users
 * Shows: title + description + contact admin message
 */
function PlanPaywallDialogUser({
  onLogout,
  onGoHome,
}: PlanPaywallDialogContentProps) {
  const { t } = useComponentTranslation(locales)
  return (
    <UpsellDialogShell
      icon={<NoPlanIconHeader />}
      title={t('title')}
      description={t('description')}
      contactAdmin={{
        title: t('contactAdmin'),
        description: t('contactAdminDescription'),
      }}
      actions={<ExitButtons onGoHome={onGoHome} onLogout={onLogout} />}
    />
  )
}

/**
 * PlanPaywallDialogAdmin - For administrators who can subscribe
 * Shows: title + description + subscribe CTA button
 */
function PlanPaywallDialogAdmin({
  onLogout,
  onSubscribe,
  isFormerPaidSubscriber,
}: PlanPaywallDialogAdminProps) {
  const { t } = useComponentTranslation(locales)
  return (
    <UpsellDialogShell
      icon={
        <div
          className={cn(
            'mb-4 flex h-16 w-16 items-center justify-center rounded-2xl',
            isFormerPaidSubscriber
              ? 'bg-indigo-100 dark:bg-indigo-950/40'
              : 'bg-muted',
          )}
        >
          {isFormerPaidSubscriber ? (
            <IconRefresh className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
          ) : (
            <IconLock className="text-muted-foreground h-8 w-8" />
          )}
        </div>
      }
      title={t(isFormerPaidSubscriber ? 'reactivateTitle' : 'title')}
      description={t(
        isFormerPaidSubscriber ? 'reactivateDescription' : 'description',
      )}
      content={<PaywallFeatures />}
      actions={
        <>
          <Button
            onClick={onSubscribe}
            className={cn(
              'cta-shimmer relative overflow-hidden font-semibold text-white',
              visual.button,
            )}
            size="lg"
          >
            {t(isFormerPaidSubscriber ? 'reactivate' : 'subscribe')}
          </Button>
          <Button variant="ghost" onClick={onLogout} className="gap-2">
            <IconLogout className="h-4 w-4" />
            {t('logout')}
          </Button>
        </>
      }
    />
  )
}

export function PlanPaywallDialog({ open, pagePath }: PlanPaywallDialogProps) {
  const navigate = useNavigate()
  const hasTrackedOpen = useRef(false)

  const isManagedBy = useContextStore(
    (state) => state.userAccountContext?.accountInformation.isManaged,
  )

  const isAdministrator = useContextStore(
    (state) =>
      state.userAccountContext?.userAccountInformation.role === 'Administrator',
  )

  const currentPlan = useContextStore(
    (state) =>
      state.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const isFormerPaidSubscriber = useContextStore(
    (state) =>
      (state.userAccountContext?.hasHadPaidSubscription ?? false) &&
      !(state.userAccountContext?.hasActiveSubscription ?? false),
  )

  const userRole = isAdministrator ? 'Administrator' : 'User'

  const handleLogout = () => {
    trackPlanPaywallLogoutClicked({
      userRole,
      isManaged: isManagedBy ?? false,
      currentPlan,
      pagePath,
      isFormerPaidSubscriber,
    })
    navigate({ to: '/logout' })
  }

  const handleGoHome = () => {
    trackPlanPaywallHomeClicked({
      userRole,
      isManaged: isManagedBy ?? false,
      currentPlan,
      pagePath,
      isFormerPaidSubscriber,
    })
    navigate({ to: '/' })
  }

  const handleSubscribe = () => {
    trackPlanPaywallConverted({
      userRole,
      isManaged: isManagedBy ?? false,
      currentPlan,
      pagePath,
      isFormerPaidSubscriber,
    })
    navigate({ to: '/settings/billing/plans' })
  }

  // Track plan paywall opened
  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackPlanPaywallOpened({
        userRole,
        isManaged: isManagedBy ?? false,
        currentPlan,
        pagePath,
        isFormerPaidSubscriber,
      })
      hasTrackedOpen.current = true
    }

    if (!open) {
      hasTrackedOpen.current = false
    }
  }, [
    open,
    userRole,
    isManagedBy,
    currentPlan,
    pagePath,
    isFormerPaidSubscriber,
  ])

  const renderContent = () => {
    if (isManagedBy) {
      return (
        <PlanPaywallDialogManaged
          onLogout={handleLogout}
          onGoHome={handleGoHome}
        />
      )
    }

    if (isAdministrator) {
      return (
        <PlanPaywallDialogAdmin
          onLogout={handleLogout}
          onSubscribe={handleSubscribe}
          isFormerPaidSubscriber={isFormerPaidSubscriber}
        />
      )
    }

    return (
      <PlanPaywallDialogUser onLogout={handleLogout} onGoHome={handleGoHome} />
    )
  }

  return (
    <ResponsiveDialog open={open} dismissible={false}>
      <ResponsiveDialogContent size="xl">
        {renderContent()}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
